import React, {useEffect, useState} from 'react';

interface TagAutocompleteProps {
  values: string[];
  onChange: (values: string[]) => void;
  getSuggestions: (query: string) => Promise<string[]>;
  placeholder?: string;
}

const containsIgnoreCase = (list: string[], item: string) => list.some(value => value.toLowerCase() === item.toLowerCase());

// Multi-value chip input with autocomplete. Typing fetches debounced suggestions; click or Enter
// adds a tag, Backspace on an empty field removes the last. Free text is allowed. De-dupes
// case-insensitively. Suggestions are provided by the parent (skills API, static industries, ...).
export const TagAutocomplete: React.FC<TagAutocompleteProps> = ({values, onChange, getSuggestions, placeholder}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setSuggestions([]);
      setOpen(false);
      return undefined;
    }
    let active = true;
    const handle = setTimeout(async () => {
      const results = await getSuggestions(trimmed);
      if (!active) return;
      const filtered = results.filter(result => !containsIgnoreCase(values, result));
      setSuggestions(filtered);
      setOpen(filtered.length > 0);
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [query, values, getSuggestions]);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (!containsIgnoreCase(values, trimmed)) {
      onChange([...values, trimmed]);
    }
    setQuery('');
    setSuggestions([]);
    setOpen(false);
  };

  const removeTag = (tag: string) => onChange(values.filter(value => value !== tag));

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addTag(query);
    } else if (event.key === 'Backspace' && !query && values.length > 0) {
      removeTag(values[values.length - 1]);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1.5 focus-within:border-[#2678FA] focus-within:ring-1 focus-within:ring-[#2678FA]">
        {values.map(tag => (
          <span className="flex items-center gap-1 rounded-full bg-[#E9EDFF] px-2 py-0.5 text-xs font-medium text-[#2678FA]" key={tag}>
            {tag}
            <button
              aria-label={`Remove ${tag}`}
              className="leading-none text-[#2678FA] hover:text-[#1f66db]"
              onClick={() => removeTag(tag)}
              type="button">
              ×
            </button>
          </span>
        ))}
        <input
          className="min-w-[80px] flex-1 border-none bg-transparent px-1 py-0.5 text-sm text-gray-800 focus:outline-none"
          onChange={event => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={values.length ? '' : placeholder}
          value={query}
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-50 mt-1 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {suggestions.map(suggestion => (
            <li key={suggestion}>
              <button
                className="block w-full truncate px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => addTag(suggestion)}
                type="button">
                {suggestion}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TagAutocomplete;
